import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Radio, Video, AlertTriangle, Zap, Users, PhoneOff } from "lucide-react";
import { AlertModal, ConfirmModal } from "../ui";
import apiClient from "../../utils/apiClient";
import { useBranding } from "../../contexts/BrandingContext";
import { can } from "../../utils/access";
import "./LiveClassesCard.scss";

// startedAt → "just started" / "42m live" / "1h 12m live"
function liveDuration(startedAt) {
  if (!startedAt) return "live";
  const ms = Date.now() - new Date(startedAt).getTime();
  if (Number.isNaN(ms) || ms < 0) return "live";
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "just started";
  if (mins < 60) return `${mins}m live`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m live` : `${h}h live`;
}

/**
 * LiveClassesCard — replaces the old "Latest Placements" card.
 *
 * Shows which course/batch is currently using a live (Zoom) session, the
 * institute-wide concurrent-session capacity (used / limit), upcoming schedule
 * conflicts that would exceed that capacity, and an upgrade nudge that routes
 * the owner to the support team.
 *
 * The list is built server-side from `Batch.isLive`, which can get STUCK `true`
 * when a class ends abnormally (host closed Zoom directly, crash, missed
 * webhook). On mount we reconcile against Zoom via
 * `POST /dashboard/live-classes/verify`: sessions Zoom reports as not-live are
 * flagged "stale" and get an End action (POST /batches/:id/end-meeting) that
 * clears the stuck flag and releases the held host — mirroring the SA Zoom
 * monitor's verify + force-end flow.
 */
const LiveClassesCard = ({ data, navigate }) => {
  const branding = useBranding();
  const supportEmail = branding?.supportEmail || "support@lmscampus.com";
  const [showSupport, setShowSupport] = useState(false);
  const canEndClass = can("batches.run");

  const {
    limited = false,
    capacity = {},
    ongoing = [],
    liveDemos = 0,
    conflicts = [],
  } = data || {};
  const { used = 0, max = 0 } = capacity;

  // Real Zoom status keyed by batchId: 'live' | 'stale' | 'unknown'.
  const [statusByBatch, setStatusByBatch] = useState({});
  const [verifying, setVerifying] = useState(false);
  // Batches whose stuck session we've ended in this view — dropped from the list.
  const [endedIds, setEndedIds] = useState(() => new Set());
  const [confirmEnd, setConfirmEnd] = useState(null); // the ongoing item pending end-confirmation
  const [endingId, setEndingId] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  // Stable key over the live batch ids — re-verify only when the set changes.
  const ongoingKey = useMemo(
    () => ongoing.map((c) => c.batchId).join(","),
    [ongoing]
  );

  // Reconcile the DB "live" set against Zoom on mount / whenever it changes.
  useEffect(() => {
    // The live-set changed (parent refetched) — drop any prior local "ended"
    // decisions so a genuinely new session for the same batch can't stay hidden.
    setEndedIds(new Set());
    if (!ongoing.length) {
      setStatusByBatch({});
      return undefined;
    }
    let cancelled = false;
    setVerifying(true);
    apiClient
      .post("/dashboard/live-classes/verify")
      .then(({ data: res }) => {
        if (cancelled) return;
        const map = {};
        (res?.results || []).forEach((r) => {
          map[String(r.batchId)] = r.stale ? "stale" : r.checked ? "live" : "unknown";
        });
        setStatusByBatch(map);
      })
      .catch(() => {
        // Verification is best-effort — leave the card in its optimistic state.
        if (!cancelled) setStatusByBatch({});
      })
      .finally(() => {
        if (!cancelled) setVerifying(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ongoingKey, ongoing.length]);

  // Re-render every minute so "live for" timers stay fresh.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!ongoing.length) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, [ongoing.length]);

  const endStuckClass = useCallback(async (item) => {
    setEndingId(item.batchId);
    setErrorMsg("");
    try {
      await apiClient.post(`/batches/${item.batchId}/end-meeting`);
      setEndedIds((prev) => new Set(prev).add(String(item.batchId)));
    } catch (err) {
      const msg = err?.response?.data?.message || "Could not end the session. Please try again.";
      // 400 "Meeting is not live" means the flag was already cleared — treat as done.
      if (err?.response?.status === 400) {
        setEndedIds((prev) => new Set(prev).add(String(item.batchId)));
      } else {
        setErrorMsg(msg);
      }
    } finally {
      setEndingId(null);
      setConfirmEnd(null);
    }
  }, []);

  // ── Local (post-verify, post-end) view state ──
  const visibleOngoing = ongoing.filter((c) => !endedIds.has(String(c.batchId)));
  const staleCount = visibleOngoing.filter(
    (c) => statusByBatch[String(c.batchId)] === "stale"
  ).length;
  const liveConfirmedCount = visibleOngoing.length - staleCount;

  // Ended sessions no longer hold a host, so drop them from the used tally.
  const usedLive = Math.max(0, used - endedIds.size);
  const pct = max > 0 ? Math.min(100, Math.round((usedLive / max) * 100)) : 0;
  const atLimit = limited && max > 0 && usedLive >= max;
  const nearLimit = limited && max > 0 && usedLive < max && usedLive / max >= 0.8;
  const capState = atLimit ? "danger" : nearLimit ? "warn" : "ok";
  const showUpgrade = limited && (atLimit || conflicts.length > 0);

  const subtitle = limited
    ? `${usedLive} of ${max} session${max === 1 ? "" : "s"} in use`
    : visibleOngoing.length === 0
      ? "No live classes right now"
      : staleCount > 0
        ? `${liveConfirmedCount} live · ${staleCount} stale`
        : `${liveConfirmedCount} class${liveConfirmedCount === 1 ? "" : "es"} live now`;

  return (
    <div className="db-card db-lc-card">
      <div className="db-card-head">
        <div className="db-card-head-left">
          <div className={`db-card-icon db-lc-icon ${liveConfirmedCount ? "is-live" : ""}`}>
            <Radio size={18} />
          </div>
          <div>
            <div className="db-card-title">Live Classes</div>
            <div className="db-card-subtitle">
              {subtitle}
              {verifying && <span className="db-lc-verifying"> · checking Zoom…</span>}
            </div>
          </div>
        </div>
        {limited && atLimit && <span className="db-lc-chip danger">Limit reached</span>}
        {limited && !atLimit && nearLimit && <span className="db-lc-chip warn">Near limit</span>}
      </div>

      <div className="db-card-body">
        {/* Capacity meter — only when a hard concurrency cap applies */}
        {limited && (
          <div className="db-lc-capacity">
            <div className="db-lc-capacity-top">
              <span className="db-lc-capacity-label">Live session capacity</span>
              <span className={`db-lc-capacity-count ${capState}`}>{usedLive} / {max}</span>
            </div>
            <div className="db-lc-capacity-track">
              <div className={`db-lc-capacity-fill ${capState}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}

        {/* Ongoing live classes */}
        {visibleOngoing.length > 0 ? (
          <div className="db-lc-list">
            {visibleOngoing.map((c) => {
              const isStale = statusByBatch[String(c.batchId)] === "stale";
              const isEnding = endingId === c.batchId;
              return (
                <div
                  key={c.id}
                  className={`db-lc-item ${isStale ? "is-stale" : ""}`}
                  onClick={() => navigate(`/batches/${c.batchId}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === "Enter") navigate(`/batches/${c.batchId}`); }}
                >
                  <span className={`db-lc-live-dot ${isStale ? "is-stale" : ""}`} />
                  <div className="db-lc-info">
                    <div className="db-lc-course">{c.course || c.batch}</div>
                    <div className="db-lc-sub">
                      {c.batch}{c.instructor ? ` · ${c.instructor}` : ""}
                    </div>
                  </div>
                  <div className="db-lc-right">
                    {isStale ? (
                      <>
                        <span className="db-lc-stale-tag">
                          <AlertTriangle size={11} /> Not live on Zoom
                        </span>
                        {canEndClass && (
                          <button
                            type="button"
                            className="db-lc-end-btn"
                            disabled={isEnding}
                            onClick={(e) => { e.stopPropagation(); setConfirmEnd(c); }}
                          >
                            <PhoneOff size={12} /> {isEnding ? "Ending…" : "End"}
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="db-lc-timer">{liveDuration(c.startedAt)}</div>
                        <div className="db-lc-students">
                          <Users size={11} /> {c.students}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="db-lc-empty">
            <Video size={22} />
            <span>No live classes right now</span>
          </div>
        )}

        {liveDemos > 0 && (
          <div className="db-lc-demos">
            +{liveDemos} live demo session{liveDemos === 1 ? "" : "s"} also using capacity
          </div>
        )}

        {/* Schedule conflicts */}
        {conflicts.length > 0 && (
          <div className="db-lc-conflicts">
            <div className="db-lc-conflicts-head">
              <AlertTriangle size={13} /> Schedule conflicts today
            </div>
            {conflicts.map((cf, i) => (
              <div className="db-lc-conflict" key={`${cf.start}-${i}`}>
                <div className="db-lc-conflict-top">
                  <span className="db-lc-conflict-time">{cf.start}–{cf.end}</span>
                  <span className="db-lc-conflict-req">
                    {cf.required} classes · {cf.capacity} slot{cf.capacity === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="db-lc-conflict-batches">
                  {cf.batches.map((b) => (
                    <span className="db-lc-conflict-chip" key={b.id}>{b.name}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Upgrade CTA → routes to support */}
        {showUpgrade && (
          <button className="db-lc-upgrade" onClick={() => setShowSupport(true)}>
            <Zap size={14} /> Upgrade for more live sessions
          </button>
        )}
      </div>

      <ConfirmModal
        show={!!confirmEnd}
        onHide={() => { if (!endingId) setConfirmEnd(null); }}
        onConfirm={() => confirmEnd && endStuckClass(confirmEnd)}
        title="End this class?"
        message={
          confirmEnd
            ? `Zoom reports "${confirmEnd.course || confirmEnd.batch}" is no longer live. Ending it will clear the stuck live status and free the session slot.`
            : ""
        }
        confirmText="End Class"
        cancelText="Cancel"
        variant="warning"
        loading={!!endingId}
      />

      <AlertModal
        show={!!errorMsg}
        onClose={() => setErrorMsg("")}
        variant="danger"
        title="Couldn't end session"
        buttonText="Close"
        message={errorMsg}
      />

      <AlertModal
        show={showSupport}
        onClose={() => setShowSupport(false)}
        variant="info"
        title="Upgrade your live sessions"
        buttonText="Close"
        message={
          <div className="db-lc-support">
            <p>
              You can run {max} live session{max === 1 ? "" : "s"} at a time. To hold more classes
              simultaneously, our team can help you increase your live-session capacity.
            </p>
            <p className="db-lc-support-label">Contact our support team:</p>
            <a className="db-lc-support-link" href={`mailto:${supportEmail}`}>{supportEmail}</a>
          </div>
        }
      />
    </div>
  );
};

export default LiveClassesCard;
